import { forwardRef } from "react";
import PropTypes from "prop-types";
import { useTheme, styled } from "@mui/material/styles";
import Button from "@mui/material/Button";

const CustomButtonRoot = styled(Button)(({ theme, ownerState }) => {
  const { palette = {}, borders = {}, boxShadows = {} } = theme;
  const {
    variant = "contained",
    bgColor = "primary",
    color = "light",
    borderRadius = "medium",
    shadow = "none",
    hoverEffect = true,
    size = "medium",
    fullWidth = false,
  } = ownerState;

  // Background and color values
  const backgroundValue = variant === "contained" 
    ? palette[bgColor]?.main || bgColor 
    : "transparent";

  const colorValue = palette[color]?.main || color;

  // Border radius value
  const borderRadiusValue = borders[borderRadius] || borderRadius;

  // Shadow value
  const boxShadowValue = boxShadows[shadow] || shadow;

  // Hover effect
  const hoverStyles = hoverEffect
    ? {
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: boxShadows.md || "0px 4px 8px rgba(0, 0, 0, 0.2)",
        },
      }
    : {};

  return {
    backgroundColor: backgroundValue,
    color: colorValue,
    borderRadius: borderRadiusValue,
    boxShadow: boxShadowValue,
    width: fullWidth ? "100%" : undefined,
    ...hoverStyles,
    "&:hover": {
      backgroundColor: variant === "contained" 
        ? palette[bgColor]?.dark || backgroundValue 
        : "rgba(0, 0, 0, 0.04)",
    },
  };
});

const CustomButton = forwardRef((props, ref) => {
  const {
    variant = "contained",
    bgColor = "primary",
    color = "light",
    borderRadius = "medium",
    shadow = "none",
    hoverEffect = true,
    size = "medium",
    fullWidth = false,
    sx,
    ...rest
  } = props;

  const theme = useTheme();

  return (
    <CustomButtonRoot
      ref={ref}
      variant={variant}
      size={size}
      ownerState={{
        variant,
        bgColor,
        color,
        borderRadius,
        shadow,
        hoverEffect,
        size,
        fullWidth,
      }}
      sx={sx}
      {...rest}
    />
  );
});

CustomButton.propTypes = {
  variant: PropTypes.oneOf(["contained", "outlined", "text"]),
  bgColor: PropTypes.string,
  color: PropTypes.string,
  borderRadius: PropTypes.string,
  shadow: PropTypes.string,
  hoverEffect: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  fullWidth: PropTypes.bool,
  sx: PropTypes.object,
};

export default CustomButton;